import { useEffect, useState } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import style from './GeneralLayout.module.css';
import { NavPanel } from './components/NavPanel';
import { NoInstitutionsPlaceholder } from './components/NoInstitutionsPlaceholder';
import { NoOrganizationsScreen } from './components/NoOrganizationsScreen';
import { CallRequests } from './components/CallRequests';
import { NotificationOnMeeting } from './components/NotificationOnMeeting';
import { TopPanel } from './components/TopPanel';
import { pathWithoutNavBarAndTopPanel } from './constants';
import { useActiveNavigation } from '@/hooks/useActiveNavigation';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useNavigationItems } from '@/hooks/useNavigationItems';
import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { useUsersStore } from '@/stores/users';
import { ERouterNames, EUserRoles, ORGANIZATION_ROUTES } from '@/types';

const customBgColors: Record<string, Partial<Record<EUserRoles, string>>> = {
  '/dashboard': {
    [EUserRoles.PRESENTER]: 'var(--colors-gray50)',
  },
};

/**
 * General layout component that includes navigation panel, top panel and an outlet for nested routes.
 * Note: NavPanel is positioned at the bottom for small screens to prevent layout overlap issues.
 * @returns The GeneralLayout component.
 */
export const GeneralLayout = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width: 1280px)');
  const [bgColor, setBgColor] = useState('#FFF');
  const location = useLocation();
  const { role, isSuperAdmin, isPresenter } = useUsersStore((state) => ({
    role: state.role,
    isSuperAdmin: state.checkIsSuperAdmin(),
    isPresenter: state.hasAnyRoles(EUserRoles.PRESENTER),
  }));
  const organizations = useOrganizationsStore((state) => state.organizations);
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );

  const isOrganizationPage = ORGANIZATION_ROUTES.includes(location.pathname);
  const { availableItems, translatedNavItems } = useNavigationItems();
  const { activeKey, setActiveKey } = useActiveNavigation(availableItems);

  const hasNoOrganizations = isSuperAdmin && !organizations.length;
  const hasNoInstitution =
    isSuperAdmin &&
    !!organizations.length &&
    !currentInstitution &&
    !isOrganizationPage;

  const organizationPageTitles: Partial<Record<string, string>> = {
    [ERouterNames.ORGANIZATION_ADMINS]: t('nav-panel.organization-admins'),
    [ERouterNames.ORGANIZATION_REPORTING]: t(
      'nav-panel.organization-reporting',
    ),
  };

  const headerTitle = hasNoOrganizations
    ? t('nav-panel.no-organizations-title')
    : hasNoInstitution
      ? t('nav-panel.no-institutions-title')
      : organizationPageTitles[location.pathname];

  useEffect(() => {
    const check = Object.keys(customBgColors).find((key) =>
      location.pathname.includes(key),
    );

    if (check && role) {
      const roleBasedColor = customBgColors[check]?.[role] || '#FFF';
      setBgColor(roleBasedColor);
      return;
    }
    setBgColor('#FFF');
  }, [location.pathname, role]);

  if (
    pathWithoutNavBarAndTopPanel.some((path) =>
      matchPath(path, location.pathname),
    )
  ) {
    return (
      <div className={style.wrapper}>
        <NotificationOnMeeting />
        <CallRequests />
        <main className={style.main}>
          <Outlet />
        </main>
      </div>
    );
  }

  const showNavPanel = !hasNoOrganizations && !isPresenter;

  const navPanel = (
    <NavPanel
      translatedNavItems={translatedNavItems}
      activeKey={activeKey}
      setActiveKey={setActiveKey}
    />
  );

  return (
    <div className={style.wrapper}>
      {!isSmallScreen && showNavPanel && navPanel}
      <div className={style.contentWrapper}>
        <TopPanel
          headerTitle={headerTitle}
          translatedNavItems={translatedNavItems}
          activeKey={activeKey}
        />
        <NotificationOnMeeting />
        <CallRequests />
        <main
          className={style.innerWrapper}
          style={{
            backgroundColor:
              hasNoOrganizations || hasNoInstitution ? '#F5F7FA' : bgColor,
          }}
        >
          {hasNoOrganizations ? (
            <NoOrganizationsScreen />
          ) : hasNoInstitution ? (
            <NoInstitutionsPlaceholder />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      {isSmallScreen && showNavPanel && navPanel}
    </div>
  );
};
